// Where "back" goes from a device screen.
//
// The app's structure is LOCATION → ROOM → DEVICES → DEVICE, and back should
// retrace it. It did not: every title bar called `navigate(-1)`, which is
// browser history, not structure. Those are the same thing only when the user
// arrived the long way round. They differ whenever:
//
//   • the device was opened from the ROOMS screen — either a common device in
//     the top strip or a device listed under its room — so back landed on the
//     location page, skipping the room entirely
//   • the device was opened from Favourites or a search, so back went there
//   • the screen was deep-linked or the app was reloaded on it, so there IS no
//     history entry — on Android that exits the app instead of going back
//
// So the target is computed from the DEVICE, not from where the user came from.
//
// ⚠️ TWO SPELLINGS OF THE SAME FIELD, AND BOTH ARE REAL. The room endpoints
// return `RoomID` while GetDeviceDetailbyUniqueNumber returns `RoomId`, and the
// device screen may hold a row from either — it seeds from the list row passed
// in `state` and then replaces it with the detail fetch. Reading only one
// spelling makes back work before the fetch lands and break after it, which is
// the kind of bug that gets reported as "sometimes".

/** RoomId 0 is a REAL value meaning "belongs to the location", not a missing one. */
export const COMMON_ROOM_ID = 0;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const roomIdOf = (device) => num(device?.RoomID ?? device?.RoomId);
export const locationIdOf = (device) => num(device?.LocationID ?? device?.LocationId);

/**
 * -> { path, state } for react-router, or null when the device knows neither
 *    its room nor its location and history is the only honest answer.
 *
 * ⚠️ `locationId` IS PASSED ON PURPOSE AND IS LOAD-BEARING. DevicesScreen feeds
 * it to getDevicesByRoom, and without it that call degrades to the bare room
 * endpoint — which drops whole device types server-side, so the room comes back
 * missing a curtain or a sensor with no error at all. See the note on
 * getDevicesByRoom in core/api/api.js. Going "back" to a room that is quietly
 * incomplete would be a worse bug than the one this fixes.
 */
export function backTargetFor(device, from) {
  if (!device && !from) return null;
  // ⚠️ `from` WINS, AND IT HAS TO. The room endpoint's rows carry `RoomID` but
  // NO `LocationID` — probed against the live server — and DeviceScreen keeps
  // the list row it was handed rather than replacing it with the detail fetch.
  // So the device alone can name its room and not its location, and the room we
  // returned to would silently be missing device types (see below). The screen
  // that opened the device knows both, so it says so in navigation state.
  const roomId = from?.roomId != null ? num(from.roomId) : roomIdOf(device);
  const locationId = from?.locationId != null
    ? num(from.locationId) : locationIdOf(device);
  const roomName = from?.roomName || device?.RoomName || '';
  const locationName = from?.locationName || device?.LocationName || '';

  if (roomId > COMMON_ROOM_ID) {
    return {
      path: `/room/${roomId}/devices`,
      state: { roomName, locationName, locationId: locationId || undefined },
    };
  }
  // A common device belongs to the location rather than to any room, so the
  // room list IS its parent — there is no device list to go back to.
  if (locationId > 0) {
    return { path: `/rooms/${locationId}`, state: { locationName } };
  }
  return null;
}
